import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2, Search, Filter, X } from "lucide-react";
import { DeckCard } from "./DeckCard";
import { DeckPagination } from "./DeckPagination";
import type { Deck, DeckFilters } from "@/lib/api/deck-api";
import { fetchDecks } from "@/lib/api/deck-api";
import type { Division } from "@/lib/types/deck-builder";
import type { UnitMap } from "@/lib/types/deck-builder";
import { tags } from "@/lib/types/tags";
import { toast } from "sonner";

interface DeckBrowserProps {
  currentUserId?: string;
  divisions: Division[];
  unitMap: UnitMap;
  onEdit?: (deck: Deck) => void;
  onEditWithCode?: (deck: Deck) => void;
  onDelete?: (deck: Deck) => void;
  refreshKey?: number;
}

export function DeckBrowser({ currentUserId, divisions, unitMap, onEdit, onEditWithCode, onDelete, refreshKey }: DeckBrowserProps) {
  const [decks, setDecks] = useState<Deck[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<DeckFilters>({
    is_public: true,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showTagFilter, setShowTagFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const PAGE_SIZE = 20;

  // Load decks when filters, search, or page changes
  useEffect(() => {
    async function loadDecks() {
      setLoading(true);
      setError(null);
      try {
        const filterParams: DeckFilters = { 
          ...filters,
          page: currentPage,
          limit: PAGE_SIZE,
        };
        
        // Add search query if provided
        if (searchQuery.trim()) {
          filterParams.search = searchQuery.trim();
        }
        
        // Add tags filter if provided
        if (selectedTags.length > 0) {
          filterParams.tags = selectedTags.join(',');
        }
        
        if (currentUserId && filters.user_id === currentUserId) {
          // Show all user's decks (public and private)
          filterParams.user_id = currentUserId;
          delete filterParams.is_public;
        }
        
        const response = await fetchDecks(filterParams);
        setDecks(response.data);
        setTotal(response.total);
        setTotalPages(Math.ceil(response.total / PAGE_SIZE));
        setHasNextPage(response.hasNextPage);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load decks");
      } finally {
        setLoading(false);
      }
    }

    loadDecks();
  }, [filters, currentUserId, searchQuery, selectedTags, currentPage, refreshKey]);

  const handleDivisionFilter = (value: string) => {
    setCurrentPage(1); // Reset to first page when filter changes
    if (value === "all") {
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters.division_descriptor;
        return newFilters;
      });
    } else {
      setFilters(prev => ({
        ...prev,
        division_descriptor: value,
      }));
    }
  };

  const handleVisibilityFilter = (showOwn: boolean) => {
    setCurrentPage(1); // Reset to first page when filter changes
    if (showOwn && currentUserId) {
      setFilters(prev => ({
        ...prev,
        user_id: currentUserId,
        is_public: undefined,
      }));
    } else {
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters.user_id;
        newFilters.is_public = true;
        return newFilters;
      });
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleClearTags = () => {
    setSelectedTags([]);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCopy = () => {
    toast.success("Deck code copied to clipboard");
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <div className="flex flex-col gap-3">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search decks..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>
            
            {/* Filter Buttons Row */}
            <div className="flex flex-wrap gap-2">
              <Select
                value={filters.division_descriptor || "all"}
                onValueChange={handleDivisionFilter}
              >
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="All Divisions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Divisions</SelectItem>
                  {divisions.map((div) => (
                    <SelectItem key={div.descriptor} value={div.descriptor}>
                      {div.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {currentUserId && (
                <Button
                  variant={filters.user_id === currentUserId ? "default" : "outline"}
                  onClick={() => handleVisibilityFilter(filters.user_id !== currentUserId)}
                  className="flex-1 sm:flex-initial whitespace-nowrap text-sm"
                  size="sm"
                >
                  <Filter className="h-3.5 w-3.5 mr-1.5 sm:mr-2" />
                  <span className="hidden sm:inline">{filters.user_id === currentUserId ? "My Decks" : "All Decks"}</span>
                  <span className="sm:hidden">{filters.user_id === currentUserId ? "Mine" : "All"}</span>
                </Button>
              )}

              <Button
                variant={selectedTags.length > 0 ? "default" : "outline"}
                onClick={() => setShowTagFilter(!showTagFilter)}
                className="flex-1 sm:flex-initial whitespace-nowrap text-sm"
                size="sm"
              >
                <Filter className="h-3.5 w-3.5 mr-1.5 sm:mr-2" />
                Tags {selectedTags.length > 0 && `(${selectedTags.length})`}
              </Button>
            </div>
          </div>

          {/* Tag Filter Panel */}
          {showTagFilter && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <Label className="text-xs sm:text-sm font-medium">Filter by Tags</Label>
                {selectedTags.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearTags}
                    className="h-7 text-xs"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5 sm:gap-2 max-h-48 sm:max-h-64 overflow-y-auto">
                {tags.map((tag) => (
                  <div key={tag} className="flex items-center space-x-1.5 sm:space-x-2">
                    <Checkbox
                      id={`filter-tag-${tag}`}
                      checked={selectedTags.includes(tag)}
                      onCheckedChange={() => handleTagToggle(tag)}
                      className="h-4 w-4"
                    />
                    <Label
                      htmlFor={`filter-tag-${tag}`}
                      className="text-xs sm:text-sm font-normal cursor-pointer leading-tight"
                    >
                      {tag}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Decks Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-destructive">{error}</div>
          </CardContent>
        </Card>
      ) : decks.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground">
              No decks found. {currentUserId && filters.user_id !== currentUserId && "Try showing your own decks or create a new one."}
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-2">
            {decks.map((deck) => (
                <DeckCard
                  key={deck.id}
                  deck={deck}
                  isOwner={currentUserId === deck.discord_id}
                  divisions={divisions}
                  unitMap={unitMap}
                  onEdit={onEdit}
                  onEditWithCode={onEditWithCode}
                  onDelete={onDelete}
                  onCopy={handleCopy}
                />
            ))}
          </div>
          {totalPages > 1 && (
            <Card>
              <CardContent className="p-0">
                <DeckPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  total={total}
                  limit={PAGE_SIZE}
                  onPageChange={handlePageChange}
                />
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}


